import { useEffect, useState } from "react";
import { ServiceLocations } from "./service-locations";
import { KpiSection } from "./kpi-section";
import { Location as TQSLocation } from "../../models/location";
import { LocationBanner } from "./location-banner";
import QualityIssuesCard from "./qualityIssues-card";
import UpcomingDates from "./upcoming-dates";
import TeamRecentActivity from "./team-recent-activity";
import { WeeklyTimelineDay } from "@/models/weeklyTimeline";
import { getAllWeeklyTimeline, getWeeklyTimeline } from "../../reducers/supplierSlice";
import { useAppDispatch, useAppSelector } from "../../reducers/store";

export interface Rep {
  deleted: boolean | null;
  active: boolean | null;
  id: number;
  email: string;
  type: string;
  firstName: string;
  lastName: string;
  location: string;
  cell: string;
  photoAttachment: any;
  primaryContact: boolean;
  startDate: string | null;
  endDate: string | null;
}

const initialState = {
  dateRange: {
    from: "",
    to: "",
  },
};

interface TimelineData {
  location: number;
  items: WeeklyTimelineDay[];
}

export function Home() {
  const dispatch = useAppDispatch();
  const selectedSupplier = useAppSelector((state) => state.supplier.selectedSupplier);
  const selectedProducingFacilities = useAppSelector((state) => state.supplier.selectedProducingFacilities);

  const [selectedLocation, setSelectedLocation] = useState<TQSLocation>({} as TQSLocation);
  const [opened, setOpened] = useState(false);
  const [weeklyTimelines, setWeeklyTimelines] = useState<{
    location: TQSLocation;
    timeline: WeeklyTimelineDay[];
  }[]>([]);
  const [allLocations, setAllLocations] = useState<TQSLocation[]>([]);
  const [allAssignedReps, setAllAssignedReps] = useState<Rep[]>([]);
  const [context, setContext] = useState(initialState);
  const [isLoading, setIsLoading] = useState(true);
  const [locationsLoaded, setLocationsLoaded] = useState(false);

  useEffect(() => {
    setAllLocations([]);
    setAllAssignedReps([]);
    setWeeklyTimelines([]);
  }, [selectedSupplier]);

  useEffect(() => {
    allLocations.forEach((location) => {
      location.agreement?.assignedReps.forEach((rep) => {
        if (rep && rep.representative) {
          if (
            location.agreement?.primaryRepresentative === rep.representative?.id
          ) {
            rep.representative.primaryContact = true;
          } else {
            rep.representative.primaryContact = false;
          }
        }
      });
    });

    const allReps: Rep[] = allLocations.flatMap(
      (location) =>
        location.agreement?.assignedReps
          .filter(
            (rep) =>
              rep.deleted == null &&
              (!rep.startTime ||
                (rep.startTime && new Date(rep.startTime) <= new Date())) &&
              (!rep.endTime ||
                (rep.endTime && new Date(rep.endTime) >= new Date())),
          )
          .map((rep) => rep.representative) ?? [],
    );

    setAllAssignedReps(allReps);
  }, [allLocations]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    async function getTimeline() {
      const currentDate = new Date();
      // Get Monday (start) of current week
      const fromDate = currentDate;
      fromDate.setDate(currentDate.getDate() - currentDate.getDay() + (currentDate.getDay() === 0 ? -6 : 1));
      // Get Sunday (end) of current week
      const toDate = new Date(fromDate);
      toDate.setDate(fromDate.getDate() + 6);

      // Set to start/end of day
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);

      // Format dates without timezone conversion
      const toFormatted = `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')}`;
      const fromFormatted = `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')}`;

      setContext((prevContext) => ({
        ...prevContext,
        dateRange: { from: fromFormatted, to: toFormatted },
      }));

      if (!toDate || !fromDate) return;

      try {
        if (!selectedLocation || !selectedLocation.id) {
          // Fetch timelines for all locations
          // Wait for quality alert call to fire before fetching timelines to avoid blocking
          await new Promise((resolve) => setTimeout(resolve, 100));

          const allTimelinesResult = await dispatch(getAllWeeklyTimeline({
            locationIds: allLocations.map((location) => location.id),
            supplierId: selectedSupplier?.id ?? 0,
            dateBefore: toDate,
            dateAfter: fromDate,
            signal,
            agreementIds: allLocations.map(l => l.agreement?.id ?? 0),
          }));

          if (allTimelinesResult.payload) {
            const allLocationsWithTimelines = (allTimelinesResult.payload as TimelineData[]).map((data: TimelineData) => {
              return {
                location: allLocations.find(
                  (location) => location.id === data.location,
                )!,
                timeline: data.items,
              };
            });

            setWeeklyTimelines(allLocationsWithTimelines);
          }
        } else {
          const timelineResult = await dispatch(getWeeklyTimeline({
            locationId: selectedLocation.id,
            agreementIds: [selectedLocation.agreement?.id ?? 0],
            supplierId: selectedSupplier?.id ?? 0,
            dateBefore: toDate,
            dateAfter: fromDate,
            signal,
          }));

          if (timelineResult.payload) {
            setWeeklyTimelines([{ 
              location: selectedLocation, 
              timeline: timelineResult.payload as WeeklyTimelineDay[] 
            }]);
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          console.log("Fetch request was canceled");
        } else {
          console.error("Fetch error:", error);
        }
      }
    }

    getTimeline();
    return () => {
      controller.abort(); // Abort any ongoing request if the component unmounts or dependencies change
    };
  }, [selectedLocation, allLocations, selectedSupplier]);

  const handleLocationsLoaded = (locations: TQSLocation[]) => {
    setAllLocations(locations);
    if (selectedLocation?.id && !locations.some(l => l.id === selectedLocation.id)) {
      setSelectedLocation({} as TQSLocation);
    }
    setLocationsLoaded(true);
  };

  // Add monitoring effect
  // useEffect(() => {
  //   console.log('allLocations updated:', {
  //     count: allLocations.length,
  //     timestamp: new Date().toISOString()
  //   });
  // }, [allLocations]);

  return (
    <div className="flex max-h-[100%] min-h-[100%] w-[100%] flex-row gap-x-[1%] text-[black]">
      <div className="flex max-h-[100%] max-w-[20%] flex-1 flex-col gap-4">
        <div className="flex h-[70%] max-h-[100%] flex-1 flex-col">
          <ServiceLocations
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            setAllLocations={handleLocationsLoaded}
          />
        </div>
        <div className="flex max-h-[calc(30%-20px)] flex-1 flex-col">
          <UpcomingDates
            selectedLocation={selectedLocation}
            allLocations={allLocations}
            isLoading={isLoading}
          />
        </div>
      </div>
      <div className="flex w-[80%] flex-1 flex-row overflow-hidden">
        <div className="flex max-w-[100%] flex-1 flex-col rounded-[8px] border-[1px] border-[#E5E5E5] bg-white p-[12px]">
          <LocationBanner
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            allLocations={allLocations}
          />
          <div className="flex max-h-[calc(100%-50px)] flex-1 flex-row gap-x-[12px]">
            <div className="flex max-w-[80%] flex-1 flex-col gap-y-[10px] overflow-auto pb-[10px] pt-[15px]">
              <div className="flex h-[250px] min-h-[250px] flex-1 flex-col">
                <KpiSection
                  selectedLocation={selectedLocation}
                  allLocations={allLocations}
                />
              </div>
              <div className="flex h-[calc(100%-260px)] min-h-[calc(100%-260px)] min-w-fit flex-1 flex-col">
                <QualityIssuesCard
                  selectedLocation={selectedLocation}
                  allLocations={allLocations}
                  locationsLoaded={locationsLoaded}
                />
              </div>
            </div>
            <div className="mb-3 mt-3 flex w-[20%] min-w-[300px]">
              <TeamRecentActivity
                selectedLocation={selectedLocation}
                allLocations={allLocations}
                allAssignedReps={allAssignedReps}
                timelinesWithLocations={weeklyTimelines}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
